import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Map as MaplibreMap } from 'maplibre-gl';

import { MAP_ICONS } from '../constants/map';
import { iconPickerIcons } from '../constants/icons';
import { getCategoriesSelector } from '../store/reducers/config';

export const useLoadMapIcons = (map: MaplibreMap | null) => {
  const categories = useSelector(getCategoriesSelector);

  const categoriesDynamicIcons = useMemo(() => {
    const categoriesIcons = new Set<string>();

    categories.forEach(category => {
      if (category.mainCategorySvg) {
        categoriesIcons.add(category.mainCategorySvg);
      }

      if (category.subCategorySvg) {
        categoriesIcons.add(category.subCategorySvg);
      }
    });

    return Array.from(categoriesIcons);
  }, [categories]);

  useEffect(() => {
    // We have to check style because `hasImage` will throw an error on page change because `style` is undefined
    if (map?.style) {
      categoriesDynamicIcons.forEach(iconSrc => {
        if (!map.hasImage(iconSrc)) {
          const image = new Image(50, 50);

          image.crossOrigin = 'anonymous';
          image.src = iconSrc;
          image.onload = () => map.addImage(iconSrc, image);
        }
      });

      for (const [iconName, icon] of Object.entries(MAP_ICONS)) {
        if (!map.hasImage(iconName)) {
          const image = new Image();

          image.src = icon.src;
          image.onload = () => map.addImage(iconName, image);
        }
      }

      iconPickerIcons.forEach(({ iconName, iconPath }) => {
        const darkModeIconName = `${iconName}DarkMode`;
        const lightModeIconName = `${iconName}LightMode`;

        if (!map.hasImage(darkModeIconName)) {
          const darkModeImage = new Image(16, 16);

          darkModeImage.src = iconPath.replace('.svg', '-dark-mode.svg');
          darkModeImage.onload = () => map.addImage(darkModeIconName, darkModeImage);
        }

        if (!map.hasImage(lightModeIconName)) {
          const lightModeImage = new Image(16, 16);

          lightModeImage.src = iconPath.replace('.svg', '-light-mode.svg');
          lightModeImage.onload = () => map.addImage(lightModeIconName, lightModeImage);
        }
      });
    }
  }, [map, categoriesDynamicIcons]);
};
